import { booleanAttribute, ChangeDetectionStrategy, Component, input, model } from '@angular/core';
import { ControlValueAccessor, ReactiveFormsModule, ValidationErrors, Validator } from '@angular/forms';
import { noop, provideValidator, provideValueAccessor } from '../util';

/**
 * A control that allows users to choose one or more items from a list or turn an feature on or off.
 * This is the base element, and if used directly, you must wrap it with a label.
 * This will more often be used in the CheckboxOption or CheckboxGroup component.
 *
 * @see BspkCheckboxOption
 */
@Component({
  selector: 'bspk-checkbox',
  imports: [ReactiveFormsModule],
  templateUrl: './checkbox.component.html',
  styleUrl: './checkbox.component.scss',
  host: {
    'data-bspk': 'checkbox',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideValidator(BspkCheckbox), provideValueAccessor(BspkCheckbox)],
})
export class BspkCheckbox implements ControlValueAccessor, Validator {
  readonly ariaLabel = input.required<string>({ alias: 'aria-label' });
  readonly checked = model<boolean | null>(false);
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly readOnly = input(false, { transform: booleanAttribute });
  readonly required = input(false, { transform: booleanAttribute });
  readonly indeterminate = input(false, { transform: booleanAttribute });

  protected onChange: (value: any) => void = noop;
  protected onTouched: () => void = noop;

  protected onClick(event: MouseEvent) {
    if (this.disabled() || this.readOnly()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    let checked: boolean | null;
    if (this.indeterminate()) {
      if (this.checked() === true) {
        checked = false;
      } else if (this.checked() === false) {
        checked = null;
      } else {
        checked = true;
      }
    } else {
      checked = !this.checked();
    }
    this.checked.set(checked);
    this.onChange(checked);
  }

  protected onBlur() {
    this.onTouched();
  }

  /**
   * @see ControlValueAccessor
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * @see ControlValueAccessor
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * @see ControlValueAccessor
   */
  writeValue(obj: any): void {
    this.checked.set(!!obj);
  }

  /**
   * @see ControlValueAccessor
   */
  setDisabledState(_isDisabled: boolean): void {
    // noop
  }

  /**
   * @see Validator
   */
  registerOnValidatorChange(_fn: () => void): void {
    // noop
  }

  /**
   * @see Validator
   */
  validate(): ValidationErrors | null {
    if (this.required() && !this.checked()) {
      return { required: true };
    }
    return null;
  }
}
